/**
 * MobileSidebar - Sheet-based mobile navigation sidebar
 */
import { Link, useLocation } from 'react-router-dom'
import {
  Globe,
  LayoutDashboard,
  Users,
  Package,
  ShoppingCart,
  Shield,
  Settings,
  HelpCircle,
  CreditCard,
  BarChart3,
  Bell,
  Menu,
  Link2,
  Receipt,
} from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Separator } from '../ui/separator'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet'
import { cn } from '../../lib/utils'
import { useAuth } from '@mzanzi/auth'

interface NavItem {
  label: string
  href: string
  icon: React.ElementType
  badge?: string | number
  disabled?: boolean
  staffOnly?: boolean
}

// Staff/Admin navigation
const staffMainNav: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Tenants', href: '/tenants', icon: Users, staffOnly: true },
  { label: 'Products', href: '/products', icon: Package },
  { label: 'Orders', href: '/orders', icon: ShoppingCart, badge: 3 },
  { label: 'Subscriptions', href: '/subscriptions', icon: CreditCard },
]

const staffSecondaryNav: NavItem[] = [
  { label: 'Matching', href: '/matching', icon: Link2, staffOnly: true },
  { label: 'Security', href: '/security', icon: Shield, badge: '!', staffOnly: true },
  { label: 'Analytics', href: '/analytics', icon: BarChart3, staffOnly: true },
  { label: 'Notifications', href: '/notifications', icon: Bell, badge: 12 },
]

// Customer navigation (simplified)
const customerMainNav: NavItem[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Products', href: '/products', icon: Package },
  { label: 'My Orders', href: '/orders', icon: ShoppingCart },
  { label: 'My Subscriptions', href: '/subscriptions', icon: CreditCard },
]

const customerSecondaryNav: NavItem[] = [
  { label: 'Billing', href: '/billing', icon: Receipt },
  { label: 'Notifications', href: '/notifications', icon: Bell },
]

const bottomNav: NavItem[] = [
  { label: 'Settings', href: '/settings', icon: Settings },
  { label: 'Help', href: '/support', icon: HelpCircle },
]

function NavLink({ item, isActive, onClick }: {
  item: NavItem; isActive: boolean; onClick?: () => void
}) {
  return (
    <Link
      to={item.disabled ? '#' : item.href}
      onClick={onClick}
      className={cn(
        'relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all',
        'hover:bg-accent hover:text-accent-foreground',
        isActive && 'bg-primary/10 text-primary',
        item.disabled && 'pointer-events-none opacity-50',
      )}
      title={item.label}
    >
      <item.icon className={cn('h-5 w-5 shrink-0', isActive && 'text-primary')} />
      <span className="flex-1">{item.label}</span>
      {item.badge && (
        <Badge
          variant={item.badge === '!' ? 'destructive' : 'secondary'}
          className="h-5 min-w-5 px-1.5 text-xs"
        >
          {item.badge}
        </Badge>
      )}
    </Link>
  )
}

// Mobile Sidebar (Sheet)
interface MobileSidebarProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MobileSidebar({ open, onOpenChange }: MobileSidebarProps) {
  const location = useLocation()
  const { user } = useAuth()

  const isStaff = user?.isStaff ?? false
  const mainNav = isStaff ? staffMainNav : customerMainNav
  const secondaryNav = isStaff ? staffSecondaryNav : customerSecondaryNav
  const handleNavClick = () => onOpenChange(false)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[280px] p-0">
        <SheetHeader className="h-16 border-b px-4 flex flex-row items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-blue-600">
            <Globe className="h-5 w-5 text-white" />
          </div>
          <SheetTitle className="text-xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
            Mzanzi
          </SheetTitle>
        </SheetHeader>

        <nav className="flex flex-col h-[calc(100vh-64px)]">
          <div className="flex-1 overflow-y-auto px-3 py-4">
            <p className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase">Main</p>
            <div className="space-y-1">
              {mainNav.map((item) => (
                <NavLink key={item.href} item={item} isActive={location.pathname === item.href} onClick={handleNavClick} />
              ))}
            </div>
            <Separator className="my-4" />
            <p className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase">Monitor</p>
            <div className="space-y-1">
              {secondaryNav.map((item) => (
                <NavLink key={item.href} item={item} isActive={location.pathname === item.href} onClick={handleNavClick} />
              ))}
            </div>
          </div>
          <div className="border-t px-3 py-4 space-y-1">
            {bottomNav.map((item) => (
              <NavLink key={item.href} item={item} isActive={location.pathname === item.href} onClick={handleNavClick} />
            ))}
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  )
}

export function MobileSidebarTrigger({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="ghost" size="icon" className="lg:hidden h-10 w-10" onClick={onClick}>
      <Menu className="h-5 w-5" />
      <span className="sr-only">Open menu</span>
    </Button>
  )
}
